import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import classnames from "classnames";
import { newrelic } from "@jti/fe-clients";
import { CheckoutRoutes } from "@jti/fe-common";
import { getCurrentMagentoClient } from "@jti/magento";
import { CasAddressesApi, CasProfileApi } from "@jti/rest-api";
import { createAddress as createAddressAction, DeliveryOptionType, selectAccountContact, selectAddresses, selectAddressesFetchState, selectDefaultBillingAddress, selectDefaultShippingAddress, selectShippingAddressSelected, setShippingSelectedAddressId, setShippingStepData, updateOrderAttributes, useDispatch, useSelector, } from "@jti/store";
import { Button, Form, getCountriesCodesArray, useConfig, useForm, useTranslations } from "@jti/ui";
import { DeliveryOptions } from "../../components/DeliveryOptions";
import { CustomDeliveryOption, validateStoreAddress, } from "../../components/DeliveryOptions/CustomDeliveryOption/CustomDeliveryOption";
import { InStoreDeliveryOption } from "../../components/DeliveryOptions/InStoreDeliveryOption/InStoreDeliveryOption";
import { OrderAttributes } from "../../components/OrderAttributes";
import { defaultDeliveryMethod } from "../../constants/checkout-constants";
import { useAemModel } from "../../contexts/AemModelContext";
import { useAgeVerification } from "../../hooks/useAgeVerification";
import { useDeliveryLimit } from "../../hooks/useDeliveryLimit";
import { useFreeTrial } from "../../hooks/useFreeTrial";
import { useLoyalty } from "../../hooks/useLoyalty";
import useShipping from "../../hooks/useShipping";
import { useTrialBlockingCheckout } from "../../hooks/useTrialBlockingCheckout";
import { BillingAddress } from "./components/BillingAddress";
import { ShippingAddress } from "./components/ShippingAddress";
import { ShippingContact } from "./components/ShippingContact";
import { getCurrentDeliveryOptionType, getDeliveryOptionType } from "./utils/getCurrentDeliveryOptionType";
import { getInitialDeliveryOption } from "./utils/getInitialDeliveryOption";
import { getSelectedShippingAddress } from "./utils/getSelectedShippingAddress";
import { propagateContactDataToAddress } from "./utils/propagateContactDataToAddress";
import { defaultBillingAddress, defaultShippingContact } from "./defaultShippingFormValues";
import styles from "./ShippingForm.module.scss";
export const ShippingForm = ({ asideSubmitButtonWrapperRef }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { __ } = useTranslations();
    const config = useConfig();
    const checkoutModel = useAemModel();
    const formRef = useRef(null);
    const { isFreeTrial } = useFreeTrial();
    const { isAgeVerificationError } = useAgeVerification();
    const { isTrialBlockingCheckout } = useTrialBlockingCheckout();
    const { hasCampaignSelected } = useLoyalty();
    const { cartId = "", orderDeliveryLimits } = useShipping();
    const { isDeliveryLimitError } = useDeliveryLimit();
    const accountContact = useSelector(selectAccountContact);
    const cartState = useSelector((state) => state.cart);
    const country = useSelector((state) => state.checkout.country);
    const addressesFetchState = useSelector((state) => selectAddressesFetchState(state));
    const shippingAddressSelected = useSelector((state) => selectShippingAddressSelected(state, country));
    const savedDefaultBillingAddress = useSelector((state) => selectDefaultBillingAddress(state, country));
    const addresses = useSelector(selectAddresses);
    const savedDefaultShippingAddress = useSelector((state) => selectDefaultShippingAddress(state));
    const { shippingStepData } = useSelector((state) => state.standardCheckoutForm);
    const deliveryOptions = useMemo(() => (checkoutModel.deliveryOptions.length ? checkoutModel.deliveryOptions : [defaultDeliveryMethod]), [checkoutModel.deliveryOptions]);
    const isEditingShippingAddress = !shippingAddressSelected?.id;
    const initialDeliveryOption = useMemo(() => {
        return getInitialDeliveryOption(deliveryOptions);
    }, [deliveryOptions]);
    const countriesCodesArray = useMemo(() => getCountriesCodesArray(config), [config]);
    const form = useForm({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: shippingStepData ?? {
            shippingAddress: undefined, // this should not be in the form. Selected shipping address should be defined based on delivery option on form submit
            atHomeDeliveryShippingAddress: savedDefaultShippingAddress,
            shouldUseAccountContact: true,
            shippingContact: defaultShippingContact,
            billingAddressType: "shipping",
            billingAddress: {
                ...defaultBillingAddress,
                ...country,
            },
            deliveryOptionType: getDeliveryOptionType(initialDeliveryOption),
            orderAttributes: {},
            shippingMethodCode: initialDeliveryOption.methodCode,
            shouldUseInStoreDeliveryBillingAddress: false,
            externalShippingCarrier: null,
            editPhone: {
                code: countriesCodesArray[0] || "",
                phone: "",
            },
        },
        shouldUnregister: false,
    });
    const { errors: formErrors, isSubmitting: isFormSubmitting } = form.formState;
    const shouldUseAccountContact = form.watch("shouldUseAccountContact");
    const shippingContactInput = form.watch("shippingContact");
    const shippingMethodCode = form.watch("shippingMethodCode");
    const atHomeDeliveryShippingAddressValue = form.watch("atHomeDeliveryShippingAddress");
    const billingAddressTypeValue = form.watch("billingAddressType");
    const inStoreDeliveryStoreValue = form.watch("inStoreDeliveryStore");
    const deliveryOptionTypeValue = form.watch("deliveryOptionType");
    const externalShippingCarrierValue = form.watch("externalShippingCarrier");
    const [updatedPhoneNumber, setUpdatedPhoneNumber] = useState(null);
    const { allow_empty_firstname } = cartState.storeConfig;
    const contactInformation = useMemo(() => {
        if (!shouldUseAccountContact) {
            return shippingContactInput;
        }
        const telephone = shippingContactInput?.telephone || accountContact?.telephone;
        const firstname = allow_empty_firstname ? "" : accountContact?.firstname;
        const newShippingContact = {
            ...accountContact,
            telephone: telephone ?? "",
            firstname: firstname ?? "",
            lastname: accountContact?.lastname ?? "",
        };
        return newShippingContact;
    }, [accountContact, shippingContactInput?.telephone, shouldUseAccountContact]);
    const isPhoneValidationError = useMemo(() => {
        if (/true/i.test(config.CHECKOUT_PHONE_REQUIRED) && shouldUseAccountContact) {
            return !contactInformation?.telephone && !accountContact?.telephone;
        }
        return false;
    }, [
        shouldUseAccountContact,
        contactInformation?.telephone,
        accountContact?.telephone,
        config.CHECKOUT_PHONE_REQUIRED,
    ]);
    const isValidExternalAddress = useMemo(() => {
        if (deliveryOptionTypeValue !== DeliveryOptionType.CustomDeliveryOption) {
            return true;
        }
        return Boolean(!!externalShippingCarrierValue?.address && validateStoreAddress(externalShippingCarrierValue.address));
    }, [deliveryOptionTypeValue, externalShippingCarrierValue]);
    const selectedDeliveryOption = deliveryOptions.find((option) => option.methodCode === shippingMethodCode) || defaultDeliveryMethod;
    const isSubmitEnabled = !!Object.keys(formErrors).length ||
        (deliveryOptionTypeValue === DeliveryOptionType.InStoreJti && inStoreDeliveryStoreValue == null) ||
        isDeliveryLimitError ||
        isAgeVerificationError ||
        isTrialBlockingCheckout ||
        !isValidExternalAddress ||
        !hasCampaignSelected;
    const getBillingAddressForNonHomeDelivery = (formData) => {
        const shouldUseDefaultBillingAddress = savedDefaultBillingAddress && !formData.shouldUseInStoreDeliveryBillingAddress;
        const billingAddress = shouldUseDefaultBillingAddress
            ? { ...savedDefaultBillingAddress }
            : { ...formData.billingAddress, ...contactInformation };
        return billingAddress;
    };
    const getSelectedBillingAddress = (selectedShippingAddress, formData) => {
        const billingAddress = structuredClone(selectedShippingAddress);
        const isAtHomeDelivery = formData.deliveryOptionType === DeliveryOptionType.AtHome;
        if (!config.isBillingAddressVisibleForCheckout) {
            return billingAddress;
        }
        if (!isAtHomeDelivery) {
            return getBillingAddressForNonHomeDelivery(formData);
        }
        if (isAtHomeDelivery && formData.billingAddressType !== "shipping") {
            return structuredClone({ ...formData.billingAddress, ...contactInformation });
        }
        return billingAddress;
    };
    const onShippingContactEdit = (countryCode, number, phoneVerificationUuid) => {
        setUpdatedPhoneNumber({ countryCode, number, phoneVerificationUuid });
    };
    const createAddress = async () => {
        const isDefault = addresses.length === 0;
        try {
            await dispatch(createAddressAction({
                address: {
                    ...atHomeDeliveryShippingAddressValue,
                    ...accountContact,
                    isDefaultBilling: isDefault,
                    isDefaultShipping: isDefault,
                },
            }));
        }
        catch (error) {
            // exit silently
            newrelic.noticeError(new Error(`CAS_CREATE_ADDRESS_ERROR: ${error}`));
        }
    };
    const setInStoreDeliveryStore = (store) => {
        form.setValue("inStoreDeliveryStore", store);
    };
    const setExternalShippingCarrier = (carrierData) => {
        form.setValue("externalShippingCarrier", carrierData);
    };
    const handleMobileSubmit = () => {
        formRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    };
    const handleSetShippingAddressOnCart = async (address) => {
        const magentoClient = getCurrentMagentoClient(isFreeTrial);
        try {
            await magentoClient.setShippingAddressOnCart(cartId, address);
        }
        catch (err) {
            const magentoError = {
                message: `${err || __("cart.checkout.warning.problem_with_shipping_address")}`,
                fieldName: "root.shippingAddress",
            };
            throw magentoError;
        }
    };
    const handleSetBillingAddressOnCart = async (address) => {
        const magentoClient = getCurrentMagentoClient(isFreeTrial);
        try {
            await magentoClient.setBillingAddressOnCart(cartId, address);
        }
        catch (err) {
            const magentoError = {
                message: `${err || __("cart.checkout.warning.prbolem_with_billing_address")}`,
                fieldName: "root.billingAddress",
            };
            throw magentoError;
        }
    };
    const handleSetShippingMethodMagentoError = (error) => {
        const typedError = error;
        if (!typedError.fieldName || !typedError.message) {
            return false;
        }
        form.setError(typedError.fieldName, {
            type: "server",
            message: `${typedError.message}`,
        });
        toast.error(typedError.message);
        return true;
    };
    const handleSubmit = async (data) => {
        const { number, countryCode, phoneVerificationUuid } = updatedPhoneNumber || {};
        const newPhoneNumber = number && countryCode ? `${countryCode}${number}` : "";
        const selectedShippingContact = structuredClone(contactInformation);
        const selectedShippingAddress = getSelectedShippingAddress(data);
        if (!selectedShippingAddress) {
            toast.error(__("cart.checkout.warning.problem_with_shipping_address"));
            return;
        }
        const selectedShippingAddressWithContact = propagateContactDataToAddress(selectedShippingAddress, selectedShippingContact);
        selectedShippingAddressWithContact.telephone = newPhoneNumber || selectedShippingContact?.telephone || "";
        if (isPhoneValidationError) {
            const fullPhoneNumber = `${countryCode}${number}`;
            try {
                await CasProfileApi.init().updatePhone(fullPhoneNumber, phoneVerificationUuid, "");
                if (shouldUseAccountContact && shippingAddressSelected?.id && fullPhoneNumber) {
                    // this request is not awaited because it's not critical for the checkout process and it could prolong the process
                    CasAddressesApi.init().updateAddress(shippingAddressSelected.id, {
                        ...shippingAddressSelected,
                        telephone: fullPhoneNumber,
                    });
                }
                if (selectedShippingContact) {
                    selectedShippingContact.telephone = fullPhoneNumber;
                }
                selectedShippingAddressWithContact.telephone = fullPhoneNumber;
            }
            catch (err) {
                toast.error(`${err?.response?.data?.message || __("cart.checkout.warning.problem_with_phone_number_update")}`);
                return;
            }
        }
        const selectedBillingAddress = getSelectedBillingAddress(selectedShippingAddressWithContact, data);
        try {
            await handleSetShippingAddressOnCart(selectedShippingAddressWithContact);
            await handleSetBillingAddressOnCart(selectedBillingAddress);
        }
        catch (error) {
            const isMagentoError = handleSetShippingMethodMagentoError(error);
            if (!isMagentoError) {
                newrelic.noticeError(new Error(`${error}`));
            }
            return;
        }
        const createAddressPromise = isEditingShippingAddress && data.deliveryOptionType === DeliveryOptionType.AtHome ? createAddress() : undefined;
        if (data.orderAttributes) {
            dispatch(updateOrderAttributes(data.orderAttributes));
        }
        dispatch(setShippingStepData({
            ...data,
            shippingAddress: selectedShippingAddressWithContact,
            shippingMethodCode: data.shippingMethodCode,
            deliveryOptionType: data.deliveryOptionType,
        }));
        const deliveryPageUrl = new CheckoutRoutes({ isFreeTrial }).delivery;
        if (createAddressPromise) {
            await createAddressPromise;
        }
        history.push(deliveryPageUrl);
    };
    useEffect(() => {
        if (addressesFetchState.isSuccess && addresses.length > 0) {
            dispatch(setShippingSelectedAddressId());
        }
    }, [dispatch, addressesFetchState.status]);
    useEffect(function onDeliveryOptionChange() {
        form.clearErrors();
        const newDeliveryOptionType = getCurrentDeliveryOptionType(deliveryOptions, shippingMethodCode);
        form.setValue("deliveryOptionType", newDeliveryOptionType);
    }, [shippingMethodCode, deliveryOptions]);
    useEffect(function resetErrorOnBillingAddressTypeChange() {
        form.clearErrors("root.billingAddress");
    }, [form.clearErrors, billingAddressTypeValue]);
    return (_jsxs(Form, { dataTestId: "shippingForm", form: form, onSubmit: handleSubmit, className: styles.container, ref: formRef, children: [_jsxs(ShippingContact, { initialContactData: contactInformation, onEdit: onShippingContactEdit, isPhoneValidationErrorVisible: isPhoneValidationError, children: [deliveryOptions.length > 1 && (_jsx(DeliveryOptions, { name: "shippingMethodCode", children: deliveryOptions.map(({ label, methodCode }) => (_jsx("div", { "data-testid": methodCode, children: _jsx(FormControlLabel, { value: methodCode, control: _jsx(Radio, { color: "primary", checked: shippingMethodCode === methodCode }), label: __(`${label}`) }) }, methodCode))) })), deliveryOptionTypeValue === DeliveryOptionType.CustomDeliveryOption &&
                        selectedDeliveryOption.widgetTagName && (_createElement(CustomDeliveryOption, { ...selectedDeliveryOption, key: shippingMethodCode, onSelectAddress: setExternalShippingCarrier })), deliveryOptionTypeValue === DeliveryOptionType.InStoreJti && (_createElement(InStoreDeliveryOption, { ...selectedDeliveryOption, key: shippingMethodCode, onStoreSelect: setInStoreDeliveryStore, selectedStore: inStoreDeliveryStoreValue }))] }), deliveryOptionTypeValue === DeliveryOptionType.AtHome && (_jsx(ShippingAddress, { orderDeliveryLimits: orderDeliveryLimits })), config.isBillingAddressVisibleForCheckout && (_jsx(BillingAddress, { billingAddressType: billingAddressTypeValue, deliveryOptionType: deliveryOptionTypeValue, errorMessage: formErrors.root?.billingAddress?.message })), _jsx("div", { children: _jsx(OrderAttributes, { target: "buttonDelivery_before" }) }), _jsx("div", { className: classnames([styles.submitButtonWrapper, styles.desktop]), "data-testid": "shipping-form-submit-button-wrapper-desktop", children: _jsx(Button, { className: styles.submitButton, isPending: isFormSubmitting, disabled: isSubmitEnabled, type: "submit", children: __("cart.checkout.next_delivery.button") }) }), asideSubmitButtonWrapperRef.current &&
                createPortal(_jsx("div", { className: classnames([styles.submitButtonWrapper, styles.mobile]), "data-testid": "shipping-form-submit-button-wrapper-mobile", children: _jsx(Button, { className: styles.submitButton, disabled: isSubmitEnabled, isPending: isFormSubmitting, type: "button", onClick: handleMobileSubmit, children: __("cart.checkout.next_delivery.button") }) }), asideSubmitButtonWrapperRef.current)] }));
};
//# sourceMappingURL=ShippingForm.js.map